import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  InviteStatus,
  ProgrammeInvitation,
  useManagedProgrammes,
  useProgrammeInvitations,
  useResendInvitation,
  useRevokeInvitation,
} from "model/useProgrammeInvitationQueries";
import { useState } from "react";

function statusChip(s: InviteStatus) {
  switch (s) {
    case "PENDING_SIGNUP":
      return <Chip size="small" label="Awaiting signup" color="warning" />;
    case "PENDING_CONSENT":
      return <Chip size="small" label="Awaiting consent" color="info" />;
    case "ACTIVE":
      return <Chip size="small" label="Active" color="success" />;
    case "EXPIRED":
      return <Chip size="small" label="Expired" />;
    case "REVOKED":
      return <Chip size="small" label="Revoked" />;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "-";
  }
}

function InvitationRow({ inv }: { inv: ProgrammeInvitation }) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();

  const isPending =
    inv.inviteStatus === "PENDING_SIGNUP" ||
    inv.inviteStatus === "PENDING_CONSENT";
  const isClosed =
    inv.inviteStatus === "REVOKED" || inv.inviteStatus === "EXPIRED";

  // For pending invites: "Resend email" nudges the existing invite.
  // For revoked/expired invites: same backend action, but labeled as
  // "Re-invite farmer" because it reactivates the invitation.
  const primaryActionLabel = isClosed ? "Re-invite farmer" : "Resend email";
  const showPrimary = isPending || isClosed;
  const showRevoke = isPending;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(180px, 2fr) 1fr 1fr 1fr 40px",
        gap: 1,
        px: 2,
        py: 1.25,
        borderBottom: "1px solid #f4f4f5",
        alignItems: "center",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {inv.acceptedUserName ?? inv.invitedEmail}
        </Typography>
        {inv.acceptedUserName && (
          <Typography variant="caption" sx={{ color: "#71717a" }}>
            {inv.invitedEmail}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" sx={{ color: "#3f3f46" }}>
        {inv.programmeName}
      </Typography>
      <Box>{statusChip(inv.inviteStatus)}</Box>
      <Typography variant="body2" sx={{ color: "#71717a" }}>
        {inv.invitedByName ? `${inv.invitedByName}, ` : ""}
        {formatDate(inv.invitedAt)}
      </Typography>
      <Box>
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          {showPrimary && (
            <MenuItem
              disabled={resend.isPending}
              onClick={async () => {
                setAnchor(null);
                await resend.mutateAsync(inv.guid);
              }}
            >
              {primaryActionLabel}
            </MenuItem>
          )}
          {showRevoke && (
            <MenuItem
              disabled={revoke.isPending}
              onClick={async () => {
                setAnchor(null);
                await revoke.mutateAsync(inv.guid);
              }}
            >
              Revoke invitation
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
}

export function PendingInvitationsList({
  onInviteClick,
}: {
  onInviteClick: () => void;
}) {
  const { data: programmes } = useManagedProgrammes();
  // We aggregate across all corporate programmes. To keep things simple we
  // query each programme in sequence (programmes per corp are typically few).
  // Reuse a single query per programme so React Query dedups them.
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="h6">Invitations</Typography>
        <Button variant="contained" size="small" onClick={onInviteClick}>
          Invite farmer
        </Button>
      </Box>
      {(programmes ?? []).map((p) => (
        <InvitationsForProgramme
          key={p.guid}
          programmeGuid={p.guid}
          programmeName={p.name}
        />
      ))}
      {programmes && programmes.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          You don't manage any programmes yet.
        </Typography>
      )}
    </Box>
  );
}

function InvitationsForProgramme({
  programmeGuid,
  programmeName,
}: {
  programmeGuid: string;
  programmeName: string;
}) {
  const { data, isLoading } = useProgrammeInvitations(programmeGuid);
  // Show only non-active rows (active farmers are already in the FarmerTable above).
  const nonActive = (data ?? []).filter((i) => i.inviteStatus !== "ACTIVE");
  if (!isLoading && nonActive.length === 0) return null;
  return (
    <Box
      sx={{
        mb: 2,
        backgroundColor: "white",
        borderRadius: 1,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          {programmeName}
        </Typography>
      </Box>
      {isLoading ? (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        nonActive.map((inv) => <InvitationRow key={inv.guid} inv={inv} />)
      )}
    </Box>
  );
}
